import { html, type TemplateResult, nothing } from 'lit';
import { customElement, property, state, query } from 'lit/decorators.js';
import { ExtBaseElement } from '../../core/ExtBaseElement';
import { openFloating, type FloatingCleanup } from '../../lib/floating';
import './ext-autocomplete.css';

export type ExtAutocompleteSize = 'sm' | 'md' | 'lg';

export interface ExtAutocompleteOption {
  value: string;
  label: string;
  icon?: string;
  hint?: string;
}

export type ExtAutocompleteSearchFn = (
  query: string
) => Promise<ExtAutocompleteOption[]>;

/**
 * <ext-autocomplete label="Paciente" .searchFn=${fn} .value=${null}/>
 *
 * Input con dropdown de resultados async. Debounce interno, loading y empty
 * states. Portal pattern para el panel.
 *
 * Eventos: ext:search (tras debounce), ext:select (al elegir), ext:clear.
 */
@customElement('ext-autocomplete')
export class ExtAutocomplete extends ExtBaseElement {
  @property({ type: String }) label = '';
  @property({ type: String }) placeholder = '';
  @property({ type: String }) hint = '';
  @property({ type: String, reflect: true }) error = '';
  @property({ type: String, reflect: true }) size: ExtAutocompleteSize = 'md';
  @property({ type: String }) icon = '';
  @property({ type: String }) name = '';
  @property({ type: Boolean, reflect: true }) required = false;
  @property({ type: Boolean, reflect: true }) disabled = false;
  @property({ type: Boolean }) clearable = true;
  @property({ type: Number }) debounceMs = 250;
  @property({ type: Number }) minChars = 2;
  @property({ type: String }) emptyLabel = 'Sin resultados';
  @property({ type: String }) loadingLabel = 'Buscando...';

  @property({ attribute: false }) value: ExtAutocompleteOption | null = null;
  @property({ attribute: false }) searchFn: ExtAutocompleteSearchFn | null = null;

  @state() private _open = false;
  @state() private _query = '';
  @state() private _results: ExtAutocompleteOption[] = [];
  @state() private _loading = false;
  @state() private _activeIndex = -1;
  @state() private _searched = false;

  @query('.ext-autocomplete__field') private _inputEl?: HTMLInputElement;
  @query('.ext-autocomplete__wrapper') private _wrapperEl?: HTMLElement;

  private _floatingCleanup: FloatingCleanup | null = null;
  private _panelEl: HTMLDivElement | null = null;
  private _debounceTimer: ReturnType<typeof setTimeout> | null = null;
  private _reqId = 0;

  protected createRenderRoot(): HTMLElement {
    return this;
  }

  connectedCallback(): void {
    super.connectedCallback();
    if (this.value) this._query = this.value.label;
  }

  disconnectedCallback(): void {
    super.disconnectedCallback();
    this._closeDropdown();
    if (this._debounceTimer) clearTimeout(this._debounceTimer);
  }

  focus(): void {
    this._inputEl?.focus();
  }

  private _onInput = (ev: Event): void => {
    const v = (ev.target as HTMLInputElement).value;
    this._query = v;
    this._searched = false;

    if (this._debounceTimer) clearTimeout(this._debounceTimer);

    if (v.length < this.minChars) {
      this._results = [];
      this._loading = false;
      if (this._open) this._renderPanel();
      return;
    }

    this._debounceTimer = setTimeout(() => this._runSearch(v), this.debounceMs);
    if (!this._open) this._openDropdown();
  };

  private async _runSearch(q: string): Promise<void> {
    if (!this.searchFn) return;
    this._emit('search', { query: q });
    this._loading = true;
    this._searched = false;
    this._renderPanel();

    const myReq = ++this._reqId;
    try {
      const results = await this.searchFn(q);
      if (myReq !== this._reqId) return; // superseded
      this._results = results;
      this._loading = false;
      this._searched = true;
      this._activeIndex = this._results.length > 0 ? 0 : -1;
      this._renderPanel();
    } catch {
      if (myReq !== this._reqId) return;
      this._results = [];
      this._loading = false;
      this._searched = true;
      this._renderPanel();
    }
  }

  private _onFocus = (): void => {
    if (!this._open && this._query.length >= this.minChars) {
      this._openDropdown();
    }
  };

  private _onKeyDown = (ev: KeyboardEvent): void => {
    if (this.disabled) return;

    if (!this._open) {
      if (ev.key === 'ArrowDown' && this._query.length >= this.minChars) {
        ev.preventDefault();
        this._openDropdown();
      }
      return;
    }

    if (ev.key === 'ArrowDown') {
      ev.preventDefault();
      this._moveActive(1);
    } else if (ev.key === 'ArrowUp') {
      ev.preventDefault();
      this._moveActive(-1);
    } else if (ev.key === 'Enter') {
      if (this._activeIndex >= 0 && this._results[this._activeIndex]) {
        ev.preventDefault();
        this._selectOption(this._results[this._activeIndex]);
      }
    } else if (ev.key === 'Escape') {
      ev.preventDefault();
      if (this._query) {
        // Esc primero limpia el query in-progress
        this._query = '';
        if (this._inputEl) this._inputEl.value = '';
      }
      this._closeDropdown();
    }
  };

  private _moveActive(delta: number): void {
    if (this._results.length === 0) return;
    const len = this._results.length;
    this._activeIndex = (this._activeIndex + delta + len) % len;
    this._updateActiveHighlight();
  }

  private _openDropdown(): void {
    if (this._open || !this._wrapperEl) return;
    this._open = true;

    const panel = document.createElement('div');
    panel.className = 'ext-autocomplete__panel';
    panel.setAttribute('role', 'listbox');
    this._panelEl = panel;
    this._renderPanel();

    this._floatingCleanup = openFloating({
      anchor: this._wrapperEl,
      panel,
      placement: 'bottom-start',
      onOutsideClick: () => this._closeDropdown(),
      onEscape: () => {
        this._closeDropdown();
        this._inputEl?.focus();
      },
    });
  }

  private _closeDropdown(): void {
    if (!this._open) return;
    this._open = false;
    this._activeIndex = -1;
    if (this._floatingCleanup) {
      this._floatingCleanup();
      this._floatingCleanup = null;
    }
    this._panelEl = null;
  }

  private _selectOption(opt: ExtAutocompleteOption): void {
    this.value = opt;
    this._query = opt.label;
    if (this._inputEl) this._inputEl.value = opt.label;
    this._emit('select', { value: opt.value, option: opt });
    this._closeDropdown();
  }

  private _clear = (ev: Event): void => {
    ev.stopPropagation();
    this.value = null;
    this._query = '';
    this._results = [];
    if (this._inputEl) this._inputEl.value = '';
    this._emit('clear');
    this._closeDropdown();
    this.focus();
  };

  private _renderPanel(): void {
    if (!this._panelEl) return;
    const panel = this._panelEl;
    panel.innerHTML = '';

    if (this._loading) {
      const row = document.createElement('div');
      row.className = 'ext-autocomplete__status';
      const sp = document.createElement('ext-spinner');
      sp.setAttribute('size', 'sm');
      row.appendChild(sp);
      const t = document.createElement('span');
      t.textContent = this.loadingLabel;
      row.appendChild(t);
      panel.appendChild(row);
      return;
    }

    if (this._query.length < this.minChars) {
      const row = document.createElement('div');
      row.className = 'ext-autocomplete__status';
      row.textContent = `Escribi al menos ${this.minChars} caracteres...`;
      panel.appendChild(row);
      return;
    }

    if (this._searched && this._results.length === 0) {
      const row = document.createElement('div');
      row.className = 'ext-autocomplete__status';
      row.textContent = this.emptyLabel;
      panel.appendChild(row);
      return;
    }

    this._results.forEach((opt, idx) => {
      const item = document.createElement('div');
      item.className = 'ext-autocomplete__option';
      item.setAttribute('role', 'option');
      if (idx === this._activeIndex) item.classList.add('is-active');

      if (opt.icon) {
        const ic = document.createElement('ext-icon');
        ic.setAttribute('name', opt.icon);
        ic.setAttribute('size', 'sm');
        ic.setAttribute('aria-hidden', 'true');
        ic.className = 'ext-autocomplete__option-icon';
        item.appendChild(ic);
      }

      const main = document.createElement('div');
      main.className = 'ext-autocomplete__option-main';

      const lbl = document.createElement('div');
      lbl.className = 'ext-autocomplete__option-label';
      lbl.textContent = opt.label;
      main.appendChild(lbl);

      if (opt.hint) {
        const h = document.createElement('div');
        h.className = 'ext-autocomplete__option-hint';
        h.textContent = opt.hint;
        main.appendChild(h);
      }
      item.appendChild(main);

      item.addEventListener('mousedown', (ev) => {
        ev.preventDefault();
        this._selectOption(opt);
      });
      item.addEventListener('mouseenter', () => {
        this._activeIndex = idx;
        this._updateActiveHighlight();
      });

      panel.appendChild(item);
    });
  }

  private _updateActiveHighlight(): void {
    if (!this._panelEl) return;
    const items = this._panelEl.querySelectorAll('.ext-autocomplete__option');
    items.forEach((it, i) => {
      it.classList.toggle('is-active', i === this._activeIndex);
    });
  }

  render(): TemplateResult {
    const hasError = !!this.error;
    const helperText = hasError ? this.error : this.hint;
    const hasValue = this.value !== null || this._query !== '';

    return html`
      ${this.label
        ? html`<label class="ext-autocomplete__label">
            ${this.label}${this.required ? html`<span class="ext-autocomplete__req" aria-hidden="true">*</span>` : nothing}
          </label>`
        : nothing}
      <div class="ext-autocomplete__wrapper" aria-invalid=${hasError ? 'true' : 'false'}>
        ${this.icon
          ? html`<ext-icon class="ext-autocomplete__icon" name="${this.icon}" size="sm" aria-hidden="true"></ext-icon>`
          : nothing}
        <input
          class="ext-autocomplete__field"
          type="text"
          role="combobox"
          aria-autocomplete="list"
          aria-expanded=${this._open ? 'true' : 'false'}
          .value=${this._query}
          placeholder=${this.placeholder || ''}
          name=${this.name || ''}
          ?required=${this.required}
          ?disabled=${this.disabled}
          @input=${this._onInput}
          @focus=${this._onFocus}
          @keydown=${this._onKeyDown}
        />
        ${this.clearable && hasValue && !this.disabled
          ? html`<button
              type="button"
              class="ext-autocomplete__clear"
              aria-label="Limpiar"
              @click=${this._clear}
              @mousedown=${(e: Event) => e.stopPropagation()}
            >
              &#x00D7;
            </button>`
          : nothing}
      </div>
      ${helperText
        ? html`<div class="ext-autocomplete__helper ${hasError ? 'is-error' : ''}">${helperText}</div>`
        : nothing}
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'ext-autocomplete': ExtAutocomplete;
  }
}
